import type { DisciplineStageLogsIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useDisciplineStageLogsStore } from '../model/stage-logs/store'

interface DisciplineStageLogsApiOptions {
  disciplineId: string | number
  cursor?: string | null
}

/** `GET /admin-api/disciplines/{discipline}/stage-logs` — newest first; a cursor appends the next page. */
export const useDisciplineStageLogsApi = async (
  options: DisciplineStageLogsApiOptions,
): Promise<DisciplineStageLogsIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplineStageLogsStore()

  const url = useAdminRoutes('admin.disciplines.api.stage-logs', pick({
    discipline_id: options.disciplineId,
    cursor: options.cursor ?? undefined,
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)
    const indexData: DisciplineStageLogsIndexData | null = data.data || null

    if (indexData) {
      if (options.cursor) store.appendLogs(indexData.logs)
      else store.setLogs(indexData.logs)
      store.setNextCursor(indexData.meta.next_cursor)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
